
import React from 'react';
import { SectionContainer } from './section/SectionContainer';
import { MessageCircleQuestion } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import CTAButton from './CTAButton';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: "Is there a free trial?",
    answer: "Yes! You get a 30-day free trial—no credit card required. You'll have full access to AskSpot's features so you can experience the platform before making a commitment."
  },
  {
    question: "Do I need technical skills to use AskSpot?",
    answer: "No! AskSpot is designed for non-technical users. Everything is no-code, with an intuitive interface that makes building AI tools simple and accessible."
  },
  {
    question: "Can I train my AI assistant with my own data?",
    answer: "Yes! You can upload PDFs, Google Sheets, FAQs, and other documents to train your AI assistant. AskSpot continuously improves based on the data you provide."
  },
  {
    question: "What platforms does AskSpot integrate with?",
    answer: "AskSpot connects seamlessly with Google Sheets, CRMs, automation platforms like Zapier, and other third-party tools through webhooks and API integrations."
  },
  {
    question: "Can I customize the look of my AI tools?",
    answer: "Yes! AskSpot offers full white-labeling, allowing you to add your own branding, logo, and even use a custom domain."
  },
  {
    question: "What kind of support do I get?",
    answer: "We provide extensive support, including a help center, live chat, and a community forum. We also offer free onboarding sessions to help you get set up."
  }
];

const FAQ = () => {
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center gap-2 mb-4">
          <MessageCircleQuestion className="text-[#003a70] h-6 w-6" />
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brand-dark">
            Still Have Questions? We've Got Answers.
          </h2>
        </div>
        <p className="text-brand-gray text-sm md:text-base lg:text-lg max-w-2xl mx-auto">
          Everything you need to know about AskSpot—before you get started.
        </p>
      </div>
      
      {/* FAQ Accordion */}
      <div className="mb-10">
        <Accordion type="single" collapsible className="w-full">
          {faqItems.map((item, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="border border-gray-200 rounded-lg mb-4 bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <AccordionTrigger className="px-5 py-4 text-left font-medium text-base md:text-lg text-[#003a70]">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-4 text-brand-gray">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
      
      {/* CTA Button */}
      <div className="text-center">
        <CTAButton 
          variant="primary" 
          className="group inline-flex items-center justify-center gap-2 whitespace-nowrap"
          to="/contact"
        >
          <span className="flex items-center whitespace-nowrap">
            Have More Questions? Contact Us! 
            <svg 
              className="h-4 w-4 ml-2 transform group-hover:translate-x-1 transition-transform" 
              xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M5 12h14"></path>
              <path d="m12 5 7 7-7 7"></path>
            </svg>
          </span>
        </CTAButton>
      </div>
    </div>
  );
};

export default FAQ;
